import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import { isSuccess } from '@/context/services/type';
import { MAX_RETRIES } from '@/features/import_export_histories/constants';
import { ImportExportHistoriesColumn } from '@/features/import_export_histories/type';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import type { CommonError } from 'ap-openapi';
import {
  getGetImportExportHistoriesListQueryKey,
  GetImportExportHistoriesListParams,
  ImportExportHistory,
  useCancelImportExportHistory as useCancelImportExportHistoryMutation,
  useGetImportExportHistoriesListSuspense,
  useGetImportExportHistorySourcesSuspense,
  useGetOfficeMembersHook,
} from 'ap-openapi';
import { useCallback } from 'react';

export type FetchImportExportHistoriesArgs = GetImportExportHistoriesListParams;

export const useIsImportExportHistoriesListFetching = (
  params?: FetchImportExportHistoriesArgs
): boolean => {
  const queryKey = getGetImportExportHistoriesListQueryKey(params);
  const count = useIsFetching({ queryKey });
  return count > 0;
};

export type FetchImportExportHistories = {
  data: ImportExportHistoriesColumn[];
  pagination: ModernPaginationProps;
  refetch: () => Promise<unknown>;
};

export const useGetImportExportHistories = (
  params?: FetchImportExportHistoriesArgs,
  refetchInterval?: number | false
): FetchImportExportHistories => {
  const { data, refetch } = useGetImportExportHistoriesListSuspense(params, {
    query: {
      refetchInterval,
      retry: MAX_RETRIES,
      retryDelay: (attemptIndex: number) =>
        1000 * Math.pow(2, attemptIndex + 1),
    },
  });

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data?.pagination
  );

  return {
    data:
      data?.data?.import_export_histories.map(
        responseToImportExportHistoryColumn
      ) ?? [],
    pagination: pagination,
    refetch,
  };
};

export const useGetImportExportHistorySources = (): BaseOption[] => {
  const { data } = useGetImportExportHistorySourcesSuspense();
  return (
    data?.data?.sources.map((source) => ({
      label: source.display_name,
      value: source.value,
    })) ?? []
  );
};

export const useGetOfficeMembersData = () => {
  const getOfficeMembersApi = useGetOfficeMembersHook();

  const fetchOfficeMembers = async (
    memberIds: string[]
  ): Promise<BaseOption[]> => {
    if (memberIds.length === 0) return [];
    try {
      const response = await getOfficeMembersApi({
        office_member_ids: memberIds,
      });

      if (response.data.office_members) {
        return response.data.office_members.map((member) => ({
          label: member.name ?? '',
          value: member.id ?? '',
        }));
      }

      return [];
    } catch {
      return [];
    }
  };

  return { fetchOfficeMembers };
};

const responseToImportExportHistoryColumn = (
  importExportHistory: ImportExportHistory
): ImportExportHistoriesColumn => {
  return {
    id: importExportHistory.id,
    type: importExportHistory.type,
    status: importExportHistory.status,
    target_data: importExportHistory.source,
    executed_at: importExportHistory.submitted_at,
    completed_at: importExportHistory.completed_at,
    name: importExportHistory?.file_name,
    byte_size: importExportHistory.import_file_byte_size,
    can_cancel:
      importExportHistory.status === 'processing' &&
      importExportHistory.type === 'import',
    can_download: importExportHistory.can_download,
    executed_by: importExportHistory.executed_by,
    error_message: importExportHistory.error_message,
  };
};

export type CancelImportExportHistorySuccess = {
  status: 'success';
};
export type CancelImportExportHistoryError = {
  status: 'error';
  errors?: CommonError;
};
export type CancelImportExportHistoryResult =
  | CancelImportExportHistorySuccess
  | CancelImportExportHistoryError;

export const useCancelImportExportHistory = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: _mutateAsync, isPending } =
    useCancelImportExportHistoryMutation({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetImportExportHistoriesListQueryKey(),
          });
        },
      },
    });
  const mutateAsync = useCallback(
    async (id: string): Promise<CancelImportExportHistoryResult> => {
      const result = await _mutateAsync({ id });
      if (isSuccess(result.status)) {
        return { status: 'success' };
      } else {
        return {
          status: 'error',
          /* eslint-disable @typescript-eslint/consistent-type-assertions */
          errors: (result as { data?: CommonError })?.data ?? {},
        };
      }
    },
    [_mutateAsync]
  );
  return { mutateAsync, isPending };
};
